import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createClient } from '@lingaai/supabase-client';

export function useRealtimeSubmissions() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const supabase = createClient();

    const channel = supabase
      .channel('submissions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'submissions',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['submissions'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}

export function useRealtimeMetrics(submissionId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!submissionId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`metrics-${submissionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'metrics',
          filter: `submission_id=eq.${submissionId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['metrics', submissionId],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [submissionId, queryClient]);
}

export function useRealtimeFiles(submissionId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!submissionId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`files-${submissionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'submission_thread',
          filter: `submission_id=eq.${submissionId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['files', submissionId],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [submissionId, queryClient]);
}

export function useRealtimeFunds(tenantId?: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    const supabase = createClient();

    const channel = supabase
      .channel('funds-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'funds',
          filter: `tenant_id=eq.${tenantId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['funds', tenantId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}
